import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback testimonials if API fails
const fallbackTestimonials = [
  {
    id: 1,
    author_name: 'Ahmet Y.',
    rating: 5,
    text: 'iPhone ekranım kırılmıştı, aynı gün içinde onarıldı. Çok memnunum, kesinlikle tavsiye ederim!',
    relative_time: '2 hafta önce',
  },
  {
    id: 2,
    author_name: 'Zeynep K.',
    rating: 5,
    text: 'Samsung telefonumun bataryası çok çabuk bitiyordu. Batarya değişimi sonrası telefon eskisi gibi oldu.',
    relative_time: '1 ay önce',
  },
  {
    id: 3,
    author_name: 'Mehmet A.',
    rating: 5,
    text: 'MacBook anakartımda sorun vardı, başka servisler çözemedi. TeslaPro çözdü, harika iş!',
    relative_time: '3 hafta önce',
  },
  {
    id: 4,
    author_name: 'Elif S.',
    rating: 5,
    text: 'Xiaomi telefonumun ekranı ve bataryası değiştirildi. Fiyat gayet uygundu, işçilik mükemmel.',
    relative_time: '1 hafta önce',
  },
  {
    id: 5,
    author_name: 'Can B.',
    rating: 5,
    text: 'iPad ekranı çatlamıştı, 2 saat içinde teslim aldım. Profesyonel ve hızlı hizmet!',
    relative_time: '2 hafta önce',
  },
  {
    id: 6,
    author_name: 'Selin T.',
    rating: 5,
    text: 'Apple Watch ekranı değişimi için gittim. Orijinal parça kullandılar, çok memnunum.',
    relative_time: '1 ay önce',
  },
];

export const TestimonialsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(4.9);
  const [totalReviews, setTotalReviews] = useState(500);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews`);
      if (response.ok) {
        const data = await response.json();
        if (data.reviews && data.reviews.length > 0) {
          setReviews(data.reviews.slice(0, 6)); // Max 6 reviews
          setOverallRating(data.overall_rating || 4.9);
          setTotalReviews(data.total_reviews || 500);
        } else {
          setReviews(fallbackTestimonials);
        }
      } else {
        setReviews(fallbackTestimonials);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setReviews(fallbackTestimonials);
    }
    setIsLoading(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayReviews = reviews.length > 0 ? reviews : fallbackTestimonials;

  return (
    <section className="py-16 lg:py-24 bg-muted/50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3"
          >
            Google Yorumları
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-4"
          >
            Google'da {overallRating} Puan
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            {totalReviews}+ mutlu müşterimizin yorumlarından bazıları
          </motion.p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayReviews.map((review, index) => (
            <motion.div
              key={review.id || index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-card rounded-2xl p-6 shadow-card border"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {review.profile_photo_url ? (
                    <img 
                      src={review.profile_photo_url} 
                      alt={review.author_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                      {getInitials(review.author_name)}
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-foreground">{review.author_name}</h4>
                    <p className="text-sm text-muted-foreground">{review.relative_time}</p>
                  </div>
                </div>
                <Quote className="w-8 h-8 text-primary/20" />
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-3">
                {[...Array(review.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>

              {/* Text */}
              <p className="text-foreground/80 line-clamp-4">{review.text}</p>
            </motion.div>
          ))}
        </div>

        {/* Google Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex items-center justify-center gap-4"
        >
          <a 
            href="https://www.google.com/maps/place/Teslagen+Cep+Telefonu+l+Bilgisayar+l+Tablet+Tamiri/@38.4551967,27.1166865,17z/data=!4m8!3m7!1s0x14bbd98b2e8ae4b1:0x1119cfc38f2ce2b4!8m2!3d38.4551967!4d27.1192614!9m1!1b1!16s%2Fg%2F11t3zxmxjn?entry=ttu"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-card px-6 py-3 rounded-full shadow-sm border hover:shadow-md transition-shadow"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="font-semibold text-foreground">Tüm Yorumları Gör</span>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="font-bold text-foreground">{overallRating}</span>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
